import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function CenterList() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const navigate = useNavigate();
  const [centersData, setCentersData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [triggerRerender, setTriggerRerender] = useState(false);

  const getCentersList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Center/GetCenterByOrganizationId?organizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCentersData(response.center);
    } catch (error) {
      console.error("Error fetching Centers:", error);
    }
  };

  useEffect(() => {
    getCentersList();
  }, [triggerRerender]);

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 250,
      },
      {
        accessorKey: "city",
        header: "City",
        // size: 180,
      },
      {
        accessorKey: "email",
        header: "Email",
        // size: 180,
      },
      {
        accessorKey: "phoneNumber",
        header: "Phone",
        // size: 180,
      },
      {
        accessorKey: "actions",
        header: "Action",
        // size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.original.id);
              }}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const handleRowClick = (row) => {
    navigate(`/center-form/${row.id}`);
  };

  const handleDeleteClick = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this center?"
    );

    if (confirmDelete) {
      const formData = {
        id: rowId,
        isDelete: true,
        deletedBy: localStorage.getItem("userId"),
      };

      try {
        const response = await post(`Center/DeleteCenterById`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.isSuccess) {
          toast.success(response.message || "Success!");
          setTriggerRerender((prev) => !prev);
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const exportToExcel = () => {
    const filteredData = centersData.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      // ID: row.id,
      Email: row.email,
      Phone: row.phoneNumber,
    }));

    console.log(filteredData);
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Centers");
    XLSX.writeFile(workbook, "Centers.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  /* eslint-disable no-unused-vars */
  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const domain = (process.env.REACT_APP_LIVE_DOMAIN_URL || "").replace(
      /\/$/,
      ""
    );

    const cleanPath = (path) => (path ? path.replace(/^\/+/, "") : "");
    const orgLogo = orgLogoPath
      ? `${domain}/ExternalImages/${cleanPath(orgLogoPath)}`
      : null;
    const centerLogo = centerLogoPath
      ? `${domain}/ExternalImages/${cleanPath(centerLogoPath)}`
      : null;

    let logoToUse = logo; // Default imported logo
    let sidebarLogoUrl = null;

    if (roleId == "2") sidebarLogoUrl = orgLogo;
    else if (roleId == "3") sidebarLogoUrl = centerLogo;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // try {
    //     doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);
    // } catch (err) {
    //     console.error("Error adding image to PDF:", err);
    //     if (logoToUse !== logo) {
    //          doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
    //     }
    // }

    const title = "Centers List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions"
    );

    const headers = includedColumns.map((col) => col.header);

    const bodyData = centersData.map((row, index) => {
      return includedColumns.map((column) => {
        if (column.accessorKey === "mrt-row-numbers") return index + 1;
        return row[column.accessorKey] || "";
      });
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("CenterList.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Centers</h2>
        <div className="ms-auto">
          <Link to="/center-form" className="btn btn-outline-primary btn-sm">
            Add New
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <CustomDatatableGrid
            columns={columns}
            data={centersData}
            onExportExcel={exportToExcel}
            onExportPDF={exportToPDF}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleRowClick(row.original),
              sx: { cursor: "pointer" },
            })}
            initialState={{
              columnPinning: { right: ["actions"] },
            }}
          />
        </div>
      </div>
    </>
  );
}

export default CenterList;
